import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import InputMask from 'react-input-mask'
import { addYears, parse } from 'date-fns'
import { Field, Formik } from 'formik'
import UnderlineHeading from '../../../../components/UnderlineText/UnderlineHeading'
import { useLabels } from '../../../../utils/hooks/useLabels/useLabels'
import InputForm from '../../../../components/InputForm/InputForm'
import FormButtons from '../../../../components/FormButtons/FormButtons'
import useFormButtons from '../../../../components/FormButtons/hooks/useFormButtons'
import { REPOSITORY } from '../../../../api/repository/Repository'
import { DatePickerField } from '../../../../components/DatePicker/DatePIckerField/DatePickerField'
import SelectorField from '../../../../components/Selector/SelectorField'
import { genderOptions } from '../../../../utils/constants'
import { useRequestStatusContext } from '../../../../utils/hooks/Context/RequestStatusContext'
import { colors } from '../../../../utils/theme/styles'
import { Fn, normalizeText } from '../../../../utils/formats/validators'
import { useLoadingContext } from '../../../../utils/hooks/Context/LoadingContext'
import { formatRut } from '../../../../utils/formats/formats'
import Warning from '../../../../components/Warning/Warning'
import iconError from '../../../../utils/assets/icons-error.svg'
function FormRowWrapper({ children, ...rest }) {
  return (
    <HStack spacing="132px" w="100%" {...rest}>
      {children}
    </HStack>
  )
}

FormRowWrapper.propTypes = {
  children: PropTypes.node,
}
export default function Individual() {
  const { sSucursal, sCentroCosto, lGrupo, sEmpresa } =
    REPOSITORY.local.company.getCompanyData()
  const { onBack } = useFormButtons()
  const navigate = useNavigate()
  const { handleOpen, handleSetTitle, handleChangeBody, handleClose } =
    useRequestStatusContext()
  const { handleOpen: handleOpenLoading, handleClose: handleCLoseLoading } =
    useLoadingContext()
  const [inputValue, setInputValue] = useState('')
  const [rutFormat, setRutFormat] = useState('')
  const [showformat, setShowFormat] = useState(true)

  const handleFormat = useCallback(() => {
    setShowFormat((prevState) => !prevState)
  }, [])
  const { texts } = useLabels()
  const onContinue = (code) => {
    if (code === 200) {
      navigate('/account')
    }
    handleClose()
  }

  const defaultID = useMemo(() => {
    // El grupo "Libre" por defecto se busca de forma tolerante (sin distinguir
    // mayúsculas/tildes) porque el label viene de sDescripcion del backend y su
    // casing no es fiable. Mismo criterio que las cargas masivas (normalizeText).
    const findOption = lGrupo.find((el) => normalizeText(el.label) === 'libre')
    if (findOption) {
      return findOption.id
    }
    return null
  }, [lGrupo])

  return (
    <Flex direction="column" mt="50px" mb="32px">
      <Box px={['0px', '0px', '7%']}>
        <UnderlineHeading text={texts.complete_information} variant="h5" />
      </Box>
      <Formik
        initialValues={{
          sCentroCosto: null,
          sApellidoMaterno: '',
          sSucursal: null,
          sGrupo: null,
          sTelefono: '',
          sRut: '',
          dNacimiento: null,
          sNombre: '',
          sSexo: null,
          sApellidoPaterno: '',
          sTipoEmpleado: 'PE',
          bAlimentacion: true,
        }}
        onSubmit={async (values) => {
          handleOpenLoading()

          const finalData = {
            ...values,
            sTelefono: values.sTelefono.replace('-', ''),
            sRut: rutFormat.replace(/[.]+/g, ''),
          }

          if (finalData.sSucursal) {
            const branchOffice = sSucursal.find(
              (element) => element.id === finalData.sSucursal,
            )
            finalData.sSucursal = branchOffice?.label || null
          }
          if (finalData.sGrupo) {
            const group = lGrupo.find(
              (element) => element.id === finalData.sGrupo,
            )
            finalData.sGrupo = group?.label || null
          }
          if (finalData.sCentroCosto) {
            const constCenter = sCentroCosto.find(
              (element) => element.id === finalData.sCentroCosto,
            )
            finalData.sCentroCosto = constCenter?.label || null
          }
          await REPOSITORY.wsEmpresa.employees
            .createIndividualEmployee({
              data: {
                ...finalData,
                sCentroCosto: finalData?.sCentroCosto || '',
                sEmpresa,
                dNacimiento: values.dNacimiento,
                sTipoEmpleado: 'PE',
              },
            })
            .then((res) => {
              const status =
                res.data.sMensaje === ' Cuentas con observaciones. '
                  ? 406
                  : res.status
              handleSetTitle(
                ` ${res.data.lDatosCuenta[0]?.sComentario || ''}`,
                status,
              )
              handleChangeBody(
                <Button variant="primary" onClick={() => onContinue(status)}>
                  {status === 200
                    ? 'Accion realizada con éxito'
                    : 'contacta a soporte ahora'}
                </Button>,
              )
              handleOpen()
            })
            .catch((error) => {
              handleSetTitle(error.response.data.error, error.response.status)
              handleChangeBody(
                <Button
                  variant="primary"
                  onClick={() => onContinue(error.response.status)}
                >
                  {error.response.status === 200
                    ? 'Accion realizada con éxito'
                    : 'contacta a soporte ahora'}
                </Button>,
              )
              handleOpen()
            })
          handleCLoseLoading()
        }}
        validate={(values) => {
          const errors = {}
          const required = '*Requerido'
          if (!Fn.validaRut(rutFormat)) {
            errors.sRut = 'rut no valido'
          }
          if (!values.sNombre) {
            errors.sNombre = required
          }
          if (!values.sApellidoPaterno) {
            errors.sApellidoPaterno = required
          }
          if (!values.sApellidoMaterno) {
            errors.sApellidoMaterno = required
          }
          if (!values.sGrupo) {
            errors.sGrupo = required
          }
          if (!values.sSucursal) {
            errors.sSucursal = required
          }
          return errors
        }}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          errors,
          touched,
          isValid,
          /* and other goodies */
        }) => (
          <VStack m="16px" spacing="44px">
            <HStack
              w="100%"
              wrap="wrap"
              justifyContent="space-evenly"
              alignItems="flex-start"
            >
              <VStack
                maxW="616px"
                minW="300px"
                w="40%"
                spacing="24px"
                alignItems="flex-start"
              >
                <VStack alignItems="flex-start" w="100%">
                  <Box w="100%">
                    <Field
                      name="sRut"
                      id="sRut"
                      value={values.sRut}
                      onChange={handleChange}
                    >
                      {({ field }) => (
                        <InputForm
                          {...field}
                          title={texts.dni}
                          onChange={(event) => {
                            const newEvent = event
                            const actualValue = newEvent.target.value
                            if (actualValue.length <= 10) {
                              const newValue = formatRut(actualValue)
                              setInputValue(newValue.value)
                              setRutFormat(newValue.formatValue)
                              field?.onChange?.(event)
                            }
                          }}
                          value={showformat ? rutFormat : inputValue}
                          placeholder="12.345.678-9"
                          type="text"
                          variant="subtitle1"
                          onBlur={handleFormat}
                          onFocus={handleFormat}
                        />
                      )}
                    </Field>
                  </Box>
                  {!Fn.validaRut(rutFormat) ? (
                    <Box>
                      <Text color={colors.log.error} variant="body3">
                        {errors.sRut}
                      </Text>
                    </Box>
                  ) : null}
                </VStack>
                <VStack alignItems="flex-start" w="100%">
                  <Box w="100%">
                    <InputForm
                      title={texts.name}
                      name="sNombre"
                      id="sNombre"
                      value={values.sNombre}
                      onChange={handleChange}
                      variant="subtitle1"
                    />
                  </Box>
                  {errors.sNombre && touched.sNombre ? (
                    <Box>
                      <Text color={colors.log.error} variant="body3">
                        {errors.sNombre}
                      </Text>
                    </Box>
                  ) : null}
                </VStack>
                <VStack alignItems="flex-start" w="100%">
                  <Box w="100%">
                    <InputForm
                      title={texts.single_last_name}
                      name="sApellidoPaterno"
                      id="sApellidoPaterno"
                      value={values.sApellidoPaterno}
                      onChange={handleChange}
                      variant="subtitle1"
                    />
                  </Box>
                  {errors.sApellidoPaterno && touched.sApellidoPaterno ? (
                    <Box>
                      <Text color={colors.log.error} variant="body3">
                        {errors.sApellidoPaterno}
                      </Text>
                    </Box>
                  ) : null}
                </VStack>
                <VStack alignItems="flex-start" w="100%">
                  <Box w="100%">
                    <InputForm
                      title={texts.second_surname}
                      name="sApellidoMaterno"
                      id="sApellidoMaterno"
                      value={values.sApellidoMaterno}
                      onChange={handleChange}
                      variant="subtitle1"
                    />
                  </Box>
                  {errors.sApellidoMaterno && touched.sApellidoMaterno ? (
                    <Box>
                      <Text color={colors.log.error} variant="body3">
                        {errors.sApellidoMaterno}
                      </Text>
                    </Box>
                  ) : null}
                </VStack>
                <VStack alignItems="flex-start" w="100%" bgColor="#F7FAFC">
                  <Box w="100%">
                    <Field name="sEmail" id="sEmail">
                      {({ field }) => (
                        <InputForm
                          {...field}
                          title="Correo asociado a la cuenta del colaborador"
                          type="email"
                          variant="subtitle3disabled"
                          disabled
                          placeholder="Esperando correo de colaborador..."
                        />
                      )}
                    </Field>
                  </Box>
                  <Warning
                    img={iconError}
                    variant="body1"
                    warningText="Este campo se verá reflejado una vez que el colaborador cree su cuenta en la aplicación amipass."
                  />
                </VStack>
                <Box w="100%">
                  <Field
                    name="sTelefono"
                    id="sTelefono"
                    value={values.sTelefono}
                    onChange={handleChange}
                  >
                    {({ field }) => (
                      <InputForm
                        {...field}
                        title={texts.phone}
                        as={InputMask}
                        mask="9-********"
                        placeholder="9-12345678"
                        type="text"
                        variant="subtitle1"
                      />
                    )}
                  </Field>
                </Box>
                <Box w="100%">
                  <VStack alignItems="flex-start">
                    <Text variant="subtitle1">{texts.date_of_birth}</Text>
                    <DatePickerField
                      id="dNacimiento"
                      name="dNacimiento"
                      value={values.dNacimiento}
                      handleChange={handleChange}
                      maxDate={addYears(Date.now(), -10)}
                      openToDate={
                        values.dNacimiento
                          ? parse(values.dNacimiento, 'dd-MM-yyyy', new Date())
                          : addYears(Date.now(), -12)
                      }
                    />
                  </VStack>
                </Box>
                <SelectorField
                  w="100%"
                  options={genderOptions}
                  value={values.sSexo}
                  name="sSexo"
                  title={texts.gender}
                  isClearable
                  titleType="subtitle1"
                  placeholder="Seleccione un genero"
                />
              </VStack>
              <VStack
                maxW="616px"
                minW="300px"
                w="40%"
                spacing="24px"
                alignItems="flex-start"
              >
                <SelectorField
                  w="100%"
                  options={sCentroCosto}
                  name="sCentroCosto"
                  isClearable
                  value={values.sCentroCosto}
                  onChange={handleChange}
                  title={texts.cost_center}
                  titleType="subtitle1"
                  placeholder="Seleccione centro de costo"
                />
                <VStack alignItems="flex-start" w="100%">
                  <SelectorField
                    w="100%"
                    options={sSucursal}
                    name="sSucursal"
                    onChange={handleChange}
                    isClearable
                    value={values.sSucursal}
                    title={texts.branch_office}
                    titleType="subtitle1"
                    placeholder="Seleccione sucursal"
                  />
                  {errors.sSucursal && touched.sSucursal ? (
                    <Box>
                      <Text color={colors.log.error} variant="body3">
                        {errors.sSucursal}
                      </Text>
                    </Box>
                  ) : null}
                </VStack>

                <VStack alignItems="flex-start" w="100%">
                  <SelectorField
                    w="100%"
                    options={lGrupo}
                    value={values.sGrupo}
                    defaultValue={defaultID}
                    onChange={handleChange}
                    isClearable
                    name="sGrupo"
                    title={texts.group}
                    titleType="subtitle1"
                  />
                  {errors.sGrupo && touched.sGrupo ? (
                    <Box>
                      <Text color={colors.log.error} variant="body3">
                        {errors.sGrupo}
                      </Text>
                    </Box>
                  ) : null}
                </VStack>
              </VStack>
            </HStack>
            <Center mb="20px" mt="10px" w="100%">
              <FormButtons
                isForm
                isValid={isValid}
                onBack={onBack}
                onContinue={handleSubmit}
              />
            </Center>
          </VStack>
        )}
      </Formik>
    </Flex>
  )
}
